/**
 * @fileoverview RGPD Data Privacy Section component.
 * @summary Displays export data and account deletion options with improved UX.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/components/RGPDSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../hooks/redux.js'
import { logout } from '../../store/slices/authSlice.js'
import { useNavigate } from 'react-router-dom'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'
import { ConfirmModal } from '../../components/ConfirmModal'
import Spinner from '../../components/Spinner.js'

/**
 * @summary RGPD Data Privacy Section component.
 * @returns {React.ReactElement} RGPD section component.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function RGPDSection() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [isExporting, setIsExporting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteStep, setDeleteStep] = useState<'confirm' | 'final'>('confirm')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Handle export data
  const handleExportData = async () => {
    try {
      setIsExporting(true)
      setError(null)

      const response = await apiRequest({
        url: API_ENDPOINTS.USERS.EXPORT_DATA,
        method: 'GET',
      })

      if (!response.success) {
        setError(response.message || t('errors.serverError'))
        setIsExporting(false)
        return
      }

      // Export the data from response.data
      const dataToExport = response.data || {}
      const dataStr = JSON.stringify(dataToExport, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `personal-data-${new Date().toISOString().split('T')[0]}.json`
      link.click()
      URL.revokeObjectURL(url)

      setSuccess(t('rgpd.dataExportedSuccess'))
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      setError(err?.message || t('errors.serverError'))
    } finally {
      setIsExporting(false)
    }
  }

  // Handle open delete confirmation
  const handleOpenDeleteModal = () => {
    setDeleteStep('confirm')
    setDeleteModalOpen(true)
  }

  // Handle delete step 1: Initial confirmation
  const handleDeleteStep1 = () => {
    setDeleteStep('final')
  }

  // Handle delete step 2: Final deletion
  const handleDeleteFinal = async () => {
    try {
      setIsDeleting(true)
      setError(null)

      await apiRequest({
        url: API_ENDPOINTS.USERS.DELETE_ACCOUNT,
        method: 'DELETE',
      })

      setDeleteModalOpen(false)
      dispatch(logout())
      navigate('/login', { replace: true })
    } catch (err: any) {
      setError(err?.message || t('errors.serverError'))
      setIsDeleting(false)
      setDeleteModalOpen(false)
    }
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false)
    setDeleteStep('confirm')
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l-2-2m0 0l-2-2m2 2l2-2m-2 2l-2 2m6-2l2 2m0 0l2 2m-2-2l-2-2m2 2l2-2"
            />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{success}</span>
        </div>
      )}

      {/* Export Data Section */}
      <div className="card bg-base-100 dark:bg-base-800 shadow-lg border-l-4 border-primary-500">
        <div className="card-body">
          <h2 className="card-title text-xl mb-2 flex items-center gap-2">
            <span className="text-2xl">📥</span>
            {t('rgpd.exportData')}
          </h2>

          <p className="text-sm text-base-600 dark:text-base-400 mb-4">
            {t('rgpd.exportDataDescription')}
          </p>

          <div className="bg-base-50 dark:bg-base-900 p-4 rounded-lg mb-4 text-sm text-base-600 dark:text-base-400">
            <p className="font-semibold mb-2">{t('rgpd.exportIncludes')}:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{t('rgpd.exportProfile')}</li>
              <li>{t('rgpd.exportHistory')}</li>
              <li>{t('rgpd.exportEvents')}</li>
              <li>{t('rgpd.exportSubscription')}</li>
            </ul>
          </div>

          <button
            className="btn btn-primary w-full"
            onClick={handleExportData}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Spinner size="sm" />
                {t('rgpd.exporting')}
              </>
            ) : (
              <>
                📥 {t('rgpd.downloadData')}
              </>
            )}
          </button>

          <p className="text-xs text-base-400 mt-2">
            {t('rgpd.exportFormatJSON')}
          </p>
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="card bg-base-100 dark:bg-base-800 shadow-lg border-l-4 border-error-500">
        <div className="card-body">
          <h2 className="card-title text-xl mb-2 flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            {t('rgpd.deleteAccount')}
          </h2>

          <p className="text-sm text-base-600 dark:text-base-400 mb-4">
            {t('rgpd.deleteAccountDescription')}
          </p>

          <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 p-4 rounded-lg mb-4 text-sm text-error-700 dark:text-error-200">
            <p className="font-semibold mb-2">{t('rgpd.deleteWarning')}:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{t('rgpd.deleteIrreversible')}</li>
              <li>{t('rgpd.deleteAllData')}</li>
              <li>{t('rgpd.deleteNoRecovery')}</li>
            </ul>
          </div>

          <p className="text-xs text-base-600 dark:text-base-400 mb-4 italic">
            💡 {t('rgpd.deleteRecommendation')}
          </p>

          <button
            className="btn btn-error w-full"
            onClick={handleOpenDeleteModal}
            disabled={isDeleting}
          >
            🗑️ {t('rgpd.deleteAccountButton')}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal - Step 1: Initial Confirmation */}
      {deleteStep === 'confirm' && (
        <ConfirmModal
          isOpen={deleteModalOpen}
          title={t('rgpd.confirmDeletion')}
          message={t('rgpd.deleteConfirmMessage')}
          confirmLabel={t('rgpd.continueDelete')}
          onConfirm={handleDeleteStep1}
          onCancel={handleCloseDeleteModal}
          variant="danger"
        />
      )}

      {/* Delete Confirmation Modal - Step 2: Final Confirmation */}
      {deleteStep === 'final' && (
        <ConfirmModal
          isOpen={deleteModalOpen}
          title={t('rgpd.finalDeleteWarning')}
          message={t('rgpd.finalDeleteMessage')}
          confirmLabel={t('rgpd.permanentlyDelete')}
          onConfirm={handleDeleteFinal}
          onCancel={handleCloseDeleteModal}
          variant="danger"
          isLoading={isDeleting}
        />
      )}

      {/* RGPD Info Footer */}
      <div className="bg-(--color-bg-primary)/70 p-4 rounded-xl text-xs text-(--color-text-primary) dark:text-(--color-text-secondary)">
        <p className="mb-2 font-semibold">{t('rgpd.gdprCompliance')}:</p>
        <p>{t('rgpd.gdprInfo')}</p>
      </div>
    </div>
  )
}

